import { useQuery } from '@apollo/client';
import { FileText, Gift, History } from 'lucide-react';
import { useMemo } from 'react';

import { EntityDetailsHeader } from '@/components/ui/entity-details-header';
import { StatusBadge } from '@/components/ui/status-badge';
import { useDelayedLoading } from '@/hooks/useDelayedLoading';
import { useTranslation } from 'react-i18next';

import {
	DetailField,
	DetailFieldsGrid
} from '@/components/ui/details-panel.tsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AuditTab } from '@/components/ui/audit-tab';
import { CAMPAIGN_QUERY } from './campaigns.gql.tsx';

interface CampaignDetailsProps {
	campaignId: string;
	showHeader?: boolean;
	onEdit?: (id: string) => void;
	onClone?: (id: string) => void;
	onDelete?: (id: string) => void;
	onOpenInNewWindow?: (id: string) => void;
	onClose?: () => void;
	onBack?: () => void;
	showBackButton?: boolean;
}

interface CampaignRecord {
	campaignId: string;
	name: string;
	description?: string | null;
	startDate: string;
	endDate: string;
	isActive: boolean;
}

interface CampaignQueryResponse {
	campaign?: {
		campaignId: string;
		name: string;
		description?: string | null;
		startDate: string;
		endDate: string;
		isActive: boolean;
	} | null;
}

const formatDateTime = (value: string | null | undefined) => {
	if (!value) {
		return null;
	}

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return value;
	}

	return date.toLocaleString();
};

export const CampaignDetails = ({
	campaignId,
	showHeader,
	onEdit,
	onClone,
	onDelete,
	onOpenInNewWindow,
	onClose,
	onBack,
	showBackButton
}: CampaignDetailsProps) => {
	const { t } = useTranslation('common');
	const { t: tC } = useTranslation('campaigns');
	const { data, loading } = useQuery<CampaignQueryResponse>(CAMPAIGN_QUERY, {
		variables: { id: campaignId }
	});

	const { shouldShowLoading } = useDelayedLoading(loading, {
		delay: 150,
		showInitialLoading: true
	});

	const campaign = useMemo<CampaignRecord | null>(() => {
		const record = data?.campaign;
		if (!record) {
			return null;
		}

		return {
			campaignId: record.campaignId,
			name: record.name,
			description: record.description,
			startDate: record.startDate,
			endDate: record.endDate,
			isActive: record.isActive
		};
	}, [data?.campaign]);

	if (shouldShowLoading) {
		return (
			<div className="space-y-4">
				<div className="w-24 h-5 rounded animate-pulse bg-muted" />
				<div className="w-40 h-5 rounded animate-pulse bg-muted" />
				<div className="w-full h-5 rounded animate-pulse bg-muted" />
				<div className="w-2/3 h-5 rounded animate-pulse bg-muted" />
			</div>
		);
	}

	if (!campaign) {
		return (
			<div className="py-16 text-base text-center text-muted-foreground">
				{tC('details.unavailable')}
			</div>
		);
	}

	const displayName = campaign.name || tC('details.unnamedCampaign');
	const entityTypeName = campaign.isActive ? t('activeCampaign') : t('inactiveCampaign');

	return (
		<div className="flex flex-col h-full">
			{/* Header - shown when in modal mode */}
			{showHeader && (
				<EntityDetailsHeader
					icon={
						<div className="flex items-center justify-center w-14 h-14 rounded-full bg-transparent border-2 border-gray-400 dark:border-gray-500 flex-shrink-0">
							<Gift className="w-7 h-7 text-gray-600 dark:text-gray-300" />
						</div>
					}
					name={displayName}
					statusBadge={<StatusBadge isActive={campaign.isActive} />}
					entityTypeName={entityTypeName}
					entityId={campaignId}
					onEdit={onEdit}
					onClone={onClone}
					onDelete={onDelete}
					onOpenInNewWindow={onOpenInNewWindow}
					onClose={onClose}
					onBack={onBack}
					showBackButton={showBackButton}
					showCloseButton={!!onClose}
					className="pb-4 mb-4 border-b border-gray-200 dark:border-slate-700"
				/>
			)}

		<Tabs defaultValue="details" className="w-full">
			<TabsList className="gap-2 bg-transparent p-0">
				<TabsTrigger value="details" className="flex items-center gap-2 px-4 py-2 font-normal data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold">
					<FileText className="w-4 h-4" />
					<span>{t('tabs.details')}</span>
				</TabsTrigger>
				<TabsTrigger value="audit" align="right" className="flex items-center gap-2 px-4 py-2 font-normal data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold">
					<History className="w-4 h-4" />
					<span>{t('tabs.audit')}</span>
				</TabsTrigger>
			</TabsList>

			<TabsContent value="details" className="space-y-6 mt-0 pt-2">
				<div className="space-y-2.5 text-sm text-gray-700 dark:text-slate-300">
					<DetailFieldsGrid>
						<DetailField
							label={tC('details.starts')}
							value={formatDateTime(campaign.startDate)}
						/>
						<DetailField
							label={tC('details.ends')}
							value={formatDateTime(campaign.endDate)}
						/>
					</DetailFieldsGrid>
				</div>

				{campaign.description && (
					<div className="space-y-2.5 text-sm text-gray-700 dark:text-slate-300">
						<p className="text-base font-medium text-gray-700 whitespace-pre-wrap dark:text-slate-200">
							{campaign.description}
						</p>
					</div>
				)}
			</TabsContent>

			<TabsContent value="audit" className="mt-6">
				<AuditTab data={{}} />
			</TabsContent>
		</Tabs>
		</div>
	);
};
